'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import BeachMap from './BeachMap'

// Posti riservati solo a prenotazioni manuali (non prenotabili online)
const MANUAL_ONLY_SPOTS = new Set([
  'A-0-14',                        // A15
  'A-0-20', 'A-0-21', 'A-0-22',  // A9, A8, A7
  'B-0-0',  'B-0-1',  'B-0-2',   // A6, A5, A4
  'A-1-20', 'A-1-21', 'A-1-22',  // B9, B8, B7
  'B-1-0',  'B-1-1',  'B-1-2',   // B6, B5, B4
  'A-2-20', 'A-2-21', 'A-2-22',  // C9, C8, C7
  'B-2-0',  'B-2-1',  'B-2-2',   // C6, C5, C4
])
import Stepper from './Stepper'
import OrderPanel from './OrderPanel'
import { createShopBooking, createShopPaymentSession, getExtraTypeAvailability, getUmbrellaAvailability } from '../lib/api'

const IVA_RATE = 0.22
const TODAY = new Date().toISOString().split('T')[0]
const EMPTY_CHECKOUT = { email: '', phone: '', newsletter: false, nome: '', cognome: '' }

const EST_MAP = { '1-1': 'est-1', '2-1': 'est-3', '2-2': 'est-4' }
const OVEST_MAP = { '1-1': 'ov-1', '1-2': 'ov-2', '2-1': 'ov-3', '2-2': 'ov-4' }

const LINKED_EXTRA_IDS = new Set([
  'e9ac1d19-b7d0-4c7b-bbe9-29299d310a05',
  '63a04732-f821-45d7-97e3-d85f01413271',
])



function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function mapPaymentErrorCode(errorCode) {
  if (errorCode === 'PaymentAlreadyCompleted') return 'Questa prenotazione risulta gia pagata.'
  if (errorCode === 'NotAllowed') return 'La prenotazione non e in stato Pending e non puo essere pagata.'
  if (errorCode === 'StripeAccountNotConfigured') return 'Pagamento non disponibile: account Stripe non configurato.'
  return null
}

function buildKeyToUmbrellaId(availability) {
  const map = {}
  const items = availability?.data ?? []

  for (const item of items) {
    const col = parseInt(item.seat, 10)
    let key = null

    if (item.section === 1) key = `A-${item.row - 1}-${col - 1}`
    else if (item.section === 2) key = `B-${item.row - 1}-${col - 1}`
    else if (item.section === 3) key = EST_MAP[`${item.row}-${col}`] ?? null
    else if (item.section === 4) key = OVEST_MAP[`${item.row}-${col}`] ?? null

    if (key && item.id) map[key] = item.id
  }

  return map
}

function buildKeyToPrice(availability) {
  const map = {}
  const items = availability?.data ?? []

  for (const item of items) {
    const col = parseInt(item.seat, 10)
    let key = null

    if (item.section === 1) key = `A-${item.row - 1}-${col - 1}`
    else if (item.section === 2) key = `B-${item.row - 1}-${col - 1}`
    else if (item.section === 3) key = EST_MAP[`${item.row}-${col}`] ?? null
    else if (item.section === 4) key = OVEST_MAP[`${item.row}-${col}`] ?? null

    if (key && item.priceForDate != null) map[key] = item.priceForDate
  }

  return map
}

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9e9890" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)


function DateStep({ selectedDate, setSelectedDate, onNext, loading, error }) {
  return (
    <div className="date-step">
      <div className="date-input-wrapper">
        <input
          type="date"
          className="date-input"
          value={selectedDate}
          min={TODAY}
          onChange={e => setSelectedDate(e.target.value)}
        />
        <span className="date-input-icon">
          <CalendarIcon />
        </span>
      </div>
      {error && <p className="date-step-error">{error}</p>}
      <div className="date-step-actions">
        <button className="next-step-btn" disabled={!selectedDate || loading} onClick={onNext}>
          {loading ? 'Caricamento...' : 'Seleziona il tuo posto ->'}
        </button>
      </div>
    </div>
  )
}

function CheckoutForm({ form, onChange, readOnly = false }) {
  const set = (field) => (e) => onChange(field, e.target.type === 'checkbox' ? e.target.checked : e.target.value)

  return (
    <div className="checkout-form">
      <div className="form-card">
        <div className="form-section-title">Contatto</div>
        <div className="form-section-subtitle">Riceverai a questa mail la conferma della prenotazione</div>
        <div className="form-row">
          <div className="form-field">
            <label className="form-label">EMAIL *</label>
            <input type="email" className="form-input" value={form.email} onChange={set('email')} autoComplete="email" readOnly={readOnly} />
          </div>
          <div className="form-field">
            <label className="form-label">TELEFONO</label>
            <input type="tel" className="form-input" value={form.phone} onChange={(e) => onChange('phone', e.target.value.replace(/\D/g, ''))} autoComplete="tel" inputMode="numeric" readOnly={readOnly} />
          </div>
        </div>
        <div className="form-section-title">Dati cliente</div>
        <div className="form-row">
          <div className="form-field">
            <label className="form-label">NOME *</label>
            <input className="form-input" value={form.nome} onChange={(e) => { if (!/\d/.test(e.target.value)) set('nome')(e) }} autoComplete="given-name" readOnly={readOnly} />
          </div>
          <div className="form-field">
            <label className="form-label">COGNOME *</label>
            <input className="form-input" value={form.cognome} onChange={(e) => { if (!/\d/.test(e.target.value)) set('cognome')(e) }} autoComplete="family-name" readOnly={readOnly} />
          </div>
        </div>
        <label className="form-checkbox-row">
          <input type="checkbox" className="form-checkbox" checked={form.newsletter} onChange={set('newsletter')} disabled={readOnly} />
          <span className="form-checkbox-label">Voglio essere iscritto alla newsletter/marketing</span>
        </label>
      </div>
      <p className="form-invoice-note">Hai bisogno di fattura? <strong>Prenota per telefono.</strong></p>
    </div>
  )
}


export default function BookingPage() {
  const router = useRouter()
  const [step, setStep] = useState(0)
  const [selectedDate, setSelectedDate] = useState(TODAY)
  const [selected, setSelected] = useState(new Map())
  const [extrasMap, setExtrasMap] = useState({})
  const [extraTypes, setExtraTypes] = useState([])
  const [scale, setScale] = useState(1.0)
  const [availability, setAvailability] = useState(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [pricePerSpot, setPricePerSpot] = useState(null)
  const [priceError, setPriceError] = useState(null)
  const [checkoutForm, setCheckoutForm] = useState(EMPTY_CHECKOUT)
  const [checkoutError, setCheckoutError] = useState(null)
  const [paying, setPaying] = useState(false)
  const [pendingBookingId, setPendingBookingId] = useState(null)

  useEffect(() => {
    const raw = localStorage.getItem('booking_draft')
    if (!raw) return
    localStorage.removeItem('booking_draft')
    try {
      const { date, form, spots, extras, bookingId } = JSON.parse(raw)
      setSelectedDate(date)
      setCheckoutForm(form)
      setExtrasMap(extras)
      if (bookingId) setPendingBookingId(bookingId)
      setAvailabilityLoading(true)
      Promise.all([
        getUmbrellaAvailability(date),
        getExtraTypeAvailability(date),
      ]).then(([umbrellaData, extraTypeData]) => {
        const apiPrice = (umbrellaData?.data ?? []).find(u => u.priceForDate != null)?.priceForDate
        setAvailability(umbrellaData)
        setExtraTypes((extraTypeData.data ?? []).filter(e => e.isActive && e.availableQuantity > 0))
        setPricePerSpot(apiPrice ?? null)
        setSelected(new Map(spots))
        setStep(2)
      }).catch(() => setStep(0))
      .finally(() => setAvailabilityLoading(false))
    } catch {
      setStep(0)
    }
  }, [])

  const keyToUmbrellaId = useMemo(() => buildKeyToUmbrellaId(availability), [availability])
  const keyToPrice = useMemo(() => buildKeyToPrice(availability), [availability])

  const handleDateNext = async () => {
    setAvailabilityLoading(true)
    setPriceError(null)
    setSelected(new Map())
    try {
      const [umbrellaData, extraTypeData] = await Promise.all([
        getUmbrellaAvailability(selectedDate),
        getExtraTypeAvailability(selectedDate),
      ])

      const apiPrice = (umbrellaData?.data ?? []).find(u => u.priceForDate != null)?.priceForDate
      if (apiPrice == null) setPriceError('Spiaggia non prenotabile, il Bahia Caffè potrebbe essere aperto. Per disponibilità controllare su Google.')
      else setPriceError(null)

      setAvailability(umbrellaData)
      setExtraTypes((extraTypeData.data ?? []).filter(e => e.isActive && e.availableQuantity > 0))
      setPricePerSpot(apiPrice ?? null)
      setStep(1)
    } catch (err) {
      console.error('[availability] errore:', err)
      setAvailability(null)
    } finally {
      setAvailabilityLoading(false)
    }
  }

  const handleToggle = (key, label) => {
    if (pricePerSpot == null) return
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(key)) next.delete(key)
      else next.set(key, label)
      return next
    })
  }

  const handleZoom = useCallback((delta) => {
    setScale((prev) => Math.min(5.0, Math.max(1.0, +(prev + delta).toFixed(2))))
  }, [])

  const handleCheckoutChange = (field, value) => {
    setCheckoutForm(prev => ({ ...prev, [field]: value }))
  }

  const spotCount = selected.size
  const spotsTotal = [...selected.keys()].reduce((sum, k) => sum + (keyToPrice[k] ?? 0), 0)

  const linkedActive = useMemo(() =>
    [...LINKED_EXTRA_IDS].every(id => extraTypes.find(e => e.id === id)?.maxQuantity === 1),
  [extraTypes])

  // Quando spotCount diminuisce, taglia gli extra che superano il nuovo massimo
  useEffect(() => {
    if (extraTypes.length === 0) return
    setExtrasMap(prev => {
      const next = { ...prev }
      let changed = false

      for (const extra of extraTypes) {
        const cur = next[extra.id] || 0
        if (cur === 0) continue

        let max
        if (linkedActive && LINKED_EXTRA_IDS.has(extra.id)) {
          const otherId = [...LINKED_EXTRA_IDS].find(i => i !== extra.id)
          const otherQty = next[otherId] || 0
          max = Math.min(extra.availableQuantity, 1 * spotCount - otherQty)
        } else {
          max = Math.min(extra.availableQuantity, extra.maxQuantity * spotCount)
        }

        const capped = Math.max(0, Math.min(cur, max))
        if (capped !== cur) { next[extra.id] = capped; changed = true }
      }

      return changed ? next : prev
    })
  }, [spotCount])

  const handleExtras = (id, delta) => {
    const extra = extraTypes.find(e => e.id === id)
    if (!extra) return
    setExtrasMap(prev => {
      let maxQty
      if (linkedActive && LINKED_EXTRA_IDS.has(id)) {
        const otherId = [...LINKED_EXTRA_IDS].find(i => i !== id)
        const otherQty = prev[otherId] || 0
        maxQty = Math.min(extra.availableQuantity, 1 * spotCount - otherQty)
      } else {
        maxQty = Math.min(extra.availableQuantity, extra.maxQuantity * spotCount)
      }
      return {
        ...prev,
        [id]: Math.min(Math.max(maxQty, 0), Math.max(0, (prev[id] || 0) + delta)),
      }
    })
  }

  const BOOKING_FEE = 2
  const extrasTotal = extraTypes.reduce((sum, e) => sum + (extrasMap[e.id] || 0) * e.price, 0)
  const subtotal = spotsTotal + extrasTotal
  const total = subtotal + BOOKING_FEE
  const ivaAmount = total * IVA_RATE / (1 + IVA_RATE)

  const redirectToSuccess = (code, warning) => {
    sessionStorage.setItem('booking_success', JSON.stringify({
      code,
      date: selectedDate,
      spots: Array.from(selected.values()).join(','),
      extras: Object.values(extrasMap).reduce((s, q) => s + q, 0),
      total: +total.toFixed(2),
      ivaAmount: +ivaAmount.toFixed(2),
      warning: warning ?? null,
    }))
    router.push('/booking/success')
  }

  const handlePay = async () => {
    if (paying) return
    setCheckoutError(null)

    const companyId = process.env.NEXT_PUBLIC_COMPANY_ID
    if (!companyId) {
      setCheckoutError('Configurazione non valida: companyId mancante.')
      return
    }

    if (!checkoutForm.nome.trim() || !checkoutForm.cognome.trim() || !checkoutForm.email.trim()) {
      setCheckoutError('Compila nome, cognome ed email.')
      return
    }

    if (!isValidEmail(checkoutForm.email.trim())) {
      setCheckoutError('Inserisci una email valida.')
      return
    }

    const selectedKeys = [...selected.keys()]
    const umbrellaBookings = selectedKeys
      .filter(k => keyToUmbrellaId[k])
      .map(k => ({ umbrellaId: keyToUmbrellaId[k] }))

    if (umbrellaBookings.length === 0) {
      setCheckoutError('Nessun posto valido selezionato.')
      return
    }

    const extras = Object.entries(extrasMap)
      .filter(([, qty]) => qty > 0)
      .map(([extraTypeId, quantity]) => {
        const extra = extraTypes.find(e => e.id === extraTypeId)
        return {
          extraTypeId,
          extraTypeName: extra?.name ?? '',
          quantity,
          unitPrice: extra?.price ?? 0,
        }
      })

    const fullname = `${checkoutForm.nome.trim()} ${checkoutForm.cognome.trim()}`.trim()

    const booking = {
      startDate: selectedDate,
      endDate: selectedDate,
      amount: +total.toFixed(2),
      status: 0,
      type: 0,
      orderNumber: null,
      umbrellaBookings,
      extras,
      newCustomer: {
        companyId,
        fullname,
        phone: checkoutForm.phone.trim(),
        email: checkoutForm.email.trim(),
        isActive: true,
        marketingOptIn: checkoutForm.newsletter,
      },
    }

    try {
      setPaying(true)
      let bookingId = pendingBookingId
      let apiOrder = null
      if (!bookingId) {
        console.log('[shop/booking/create][request]', { companyId, booking })
        const bookingResponse = await createShopBooking(companyId, booking)
        console.log('[shop/booking/create][response]', bookingResponse)
        bookingId = bookingResponse?.data?.id
        apiOrder = bookingResponse?.data?.orderNumber ?? bookingResponse?.data?.booking?.orderNumber
        if (!bookingId) {
          setCheckoutError('Prenotazione creata ma id non ricevuto dal server.')
          return
        }
      }

      console.log('[shop/payment/create-session][request]', { companyId, bookingId })
      const paymentResponse = await createShopPaymentSession(companyId, bookingId)
      console.log('[shop/payment/create-session][response]', paymentResponse)

      const paymentErrorCode = paymentResponse?.error?.message || paymentResponse?.error?.code
      if (paymentErrorCode && paymentErrorCode !== 0 && paymentErrorCode !== '0') {
        const mappedMessage = mapPaymentErrorCode(paymentErrorCode)
        const errMsg = mappedMessage || `Errore pagamento: ${paymentErrorCode}`
        console.error('[shop/payment/create-session][business-error]', errMsg, paymentResponse)
        setCheckoutError(errMsg)
        return
      }

      const sessionUrl = paymentResponse?.data?.sessionUrl
      if (!sessionUrl) {
        console.error('[shop/payment/create-session][missing-session-url]', paymentResponse)
        setCheckoutError('Errore durante l\'avvio del pagamento. Riprova o contatta l\'assistenza.')
        return
      }

      if (!pendingBookingId) {
        sessionStorage.setItem('booking_success', JSON.stringify({
          code: apiOrder,
          date: selectedDate,
          spots: Array.from(selected.values()).join(','),
          extras: Object.values(extrasMap).reduce((s, q) => s + q, 0),
          total: +total.toFixed(2),
          ivaAmount: +ivaAmount.toFixed(2),
          warning: null,
        }))
      }

      localStorage.setItem('booking_draft', JSON.stringify({
        date: selectedDate,
        form: checkoutForm,
        spots: [...selected.entries()],
        extras: extrasMap,
        bookingId,
      }))
      window.top.location.href = sessionUrl
    } catch (err) {
      console.error('[shop/payment flow] errore:', err)
      setCheckoutError(err?.message || 'Errore durante la creazione della prenotazione/pagamento.')
    } finally {
      setPaying(false)
    }
  }

  const headers = {
    0: { h1: 'Scegli la data della tua giornata al Bahia', p: 'Consulta il calendario e verifica la disponibilita.' },
    1: { h1: 'Seleziona il tuo ombrellone sulla mappa', p: 'Visualizza la mappa interattiva e scegli il tuo ombrellone.' },
    2: { h1: 'Inserisci i dati e completa il pagamento', p: 'Compila i tuoi dati per completare la prenotazione.' },
  }
  const { h1, p } = headers[step]

  return (
    <div className="app" translate="no">
      {step === 0 && <img src="/img/Group 49.jpg" alt="Banner" className="booking-banner" />}
      <header className="page-header">
        <h1>{h1}</h1>
        {p.split('\n').map((line, i) => <p key={i}>{line}</p>)}
      </header>

      <Stepper step={step} onGoToDate={() => { setStep(0); setPriceError(null) }} />

      {step === 0 && (
        <DateStep
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onNext={handleDateNext}
          loading={availabilityLoading}
          error={priceError}
        />
      )}

      {step === 1 && (
        <>
          {priceError && <p className="date-step-error date-step-error--banner">{priceError}</p>}
          <div className="main-content">
            <div className="map-section">
              <div className="map-card">
                <BeachMap selected={selected} onToggle={handleToggle} scale={scale} onZoom={handleZoom} availability={availability} blockedSpots={MANUAL_ONLY_SPOTS} />
              </div>
              <div className="legend">
                <div className="legend-item"><span className="legend-dot" style={{ background: '#4ade80' }} />Libero</div>
                <div className="legend-item"><span className="legend-dot" style={{ background: '#fbbf24' }} />Occupato</div>
                <div className="legend-item"><span className="legend-dot" style={{ background: '#3b82f6' }} />Selezionato</div>
              </div>
              <button className="back-btn" onClick={() => { setStep(0); setPriceError(null) }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle', position: 'relative', top: '-1px' }}><polyline points="15 18 9 12 15 6" /></svg>
                Torna indietro
              </button>
            </div>
            <OrderPanel
              selected={selected}
              extraTypes={extraTypes}
              extrasMap={extrasMap}
              onExtrasChange={handleExtras}
              spotCount={spotCount}
              spotsTotal={spotsTotal}
              ivaRate={IVA_RATE}
              selectedDate={selectedDate}
              linkedExtraIds={linkedActive ? LINKED_EXTRA_IDS : null}
              onProceed={() => setStep(2)}
            />
          </div>

          <section className="info-section">
            <div className="info-title">Informazioni sulla prenotazione</div>
            <div className="info-grid">
              <div className="info-col">
                <h3>Ombrellone</h3>
                <p>Ogni postazione Ombrellone comprende SEMPRE:</p>
                <ul><li>1 Ombrellone</li><li>2 Lettini</li></ul>
              </div>
              <div className="info-col">
                <h3>Lettino / Sdraio extra</h3>
                <p>È possibile aggiungere massimo 1 lettino o 1 sdraio extra per ogni ombrellone incluso nella prenotazione. La disponibilita è limitata e soggetta a conferma.</p>
              </div>
              <div className="info-col">
                <h3>Gazebo</h3>
                <p>Due tipologie di gazebo:</p>
                <p><strong>Gazebo OVEST (lato sinistro)</strong> - 2.40m x 1.80m</p>
                <p><strong>Gazebo EST (lato destro)</strong> - 1.80m x 1.80m</p>
              </div>
            </div>
          </section>
        </>
      )}

      {step === 2 && (
        <div className="main-content">
          <CheckoutForm form={checkoutForm} onChange={handleCheckoutChange} readOnly={!!pendingBookingId} />
          <OrderPanel
            selected={selected}
            extraTypes={extraTypes}
            extrasMap={extrasMap}
            onExtrasChange={handleExtras}
            spotCount={spotCount}
            spotsTotal={spotsTotal}
            ivaRate={IVA_RATE}
            selectedDate={selectedDate}
            linkedExtraIds={linkedActive ? LINKED_EXTRA_IDS : null}
            checkout
            paying={paying}
            onBack={() => { if (pendingBookingId) { setStep(0); setSelected(new Map()); setPendingBookingId(null) } else { setStep(1) }; setCheckoutError(null) }}
            onPay={handlePay}
          />
        </div>
      )}

      {step === 2 && checkoutError && <p className="date-step-error date-step-error--banner">{checkoutError}</p>}
    </div>
  )
}
